import { Download, InsertDriveFile } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { useSwrFetch } from "hooks";
import { bytesToMb } from "utils";

import { ClientAppEvent } from "../../constants";

interface Props {
  open: boolean;
  onClose: VoidFunction;
  shipmentId: number;
  preloadedData?: DeliveryFilesResponse;
}

function getDeliveryFilesUrl(
  shipmentId: number,
  open: boolean,
  preloadedData: DeliveryFilesResponse | undefined,
): string | null {
  if (preloadedData || !open) return null;
  return `/shipments/${shipmentId}/delivery-files`;
}

export function DeliveryFilesModal(props: Props) {
  const { open, onClose, shipmentId, preloadedData } = props;
  const posthog = usePostHog();

  const {
    data: fetchedData,
    loading,
    error,
  } = useSwrFetch<DeliveryFilesResponse>({
    url: getDeliveryFilesUrl(shipmentId, open, preloadedData),
    errorMessage: "Failed to load delivery files.",
  });

  const data = preloadedData ?? fetchedData;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle component="div">
        <Typography variant="h4">Delivery attachments</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb="1rem">
          Files attached to this delivery. Visible to both the shipper and
          carrier.
        </Typography>

        {loading && (
          <Stack alignItems="center" py={2}>
            <CircularProgress />
          </Stack>
        )}

        {!loading && error && <ErrorTypography error={error} />}

        {!loading && !error && data?.files.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No files were attached to this delivery.
          </Typography>
        )}

        {!loading && !error && data && data.files.length > 0 && (
          <List
            disablePadding
            sx={{
              border: "1px solid",
              borderColor: "grey.200",
              borderRadius: 1,
            }}
          >
            {data.files.map((file, index) => (
              <ListItem
                key={file.shipment_delivery_file_pk}
                sx={{
                  borderBottom:
                    index < data.files.length - 1 ? "1px solid" : "none",
                  borderColor: "grey.200",
                }}
                secondaryAction={
                  <Tooltip title="Download — opens in a new tab">
                    <IconButton
                      edge="end"
                      aria-label={`Download ${file.filename}`}
                      size="small"
                      component="a"
                      href={file.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        posthog?.capture(
                          ClientAppEvent.SHIPMENT_DELIVERY_FILE_DOWNLOADED,
                          {
                            shipment_id: shipmentId,
                            shipment_delivery_file_pk:
                              file.shipment_delivery_file_pk,
                          },
                        )
                      }
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <InsertDriveFile color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={file.filename}
                  secondary={
                    file.size_bytes != null
                      ? `${bytesToMb(file.size_bytes)} MB · ${`${file.uploaded_by.first_name} ${file.uploaded_by.last_name ?? ""}`.trim()}`
                      : `${file.uploaded_by.first_name} ${file.uploaded_by.last_name ?? ""}`.trim()
                  }
                  primaryTypographyProps={{ variant: "body2", noWrap: true }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
